import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  AlertTriangle, Package, Search, Filter, Plus, X, Upload, Image,
  Calendar, Trash2, Edit, Eye, RefreshCw, ChevronLeft, ChevronRight,
  Grid, List, FileWarning
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Damage type options
const DAMAGE_TYPES = [
  { value: 'breakage', label: 'Breakage', color: 'bg-red-100 text-red-700' },
  { value: 'spoilage', label: 'Spoilage', color: 'bg-orange-100 text-orange-700' },
  { value: 'expiry', label: 'Expiry', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'theft', label: 'Theft', color: 'bg-purple-100 text-purple-700' },
  { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-700' }
];

const StockDamagePage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'system_admin';

  // State
  const [damages, setDamages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [viewMode, setViewMode] = useState('table');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedDamage, setSelectedDamage] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    damageType: '',
    startDate: '',
    endDate: ''
  });

  // Form state
  const [formData, setFormData] = useState({
    productId: '',
    productName: '',
    quantity: '',
    damageType: 'breakage',
    reason: '',
    evidence: []
  });

  // Product search
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const productDropdownRef = useRef(null);

  // Fetch damages
  const fetchDamages = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit
      });

      if (filters.search) params.append('search', filters.search);
      if (filters.damageType) params.append('damageType', filters.damageType);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await axios.get(`${API_URL}/stock-damages?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setDamages(response.data.damages || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || 0,
        totalPages: response.data.pagination?.totalPages || 1
      }));
    } catch (error) {
      toast.error('Failed to fetch stock damages');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    fetchDamages();
  }, [fetchDamages]);

  // Search products
  useEffect(() => {
    const searchProducts = async () => {
      if (productSearch.length < 2) {
        setProductResults([]);
        return;
      }

      setSearchingProducts(true);
      try {
        const token = localStorage.getItem('accessToken');
        const response = await axios.get(
          `${API_URL}/products?search=${productSearch}&limit=10&isActive=true`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setProductResults(response.data.products || []);
        setShowProductDropdown(true);
      } catch (error) {
        console.error('Error searching products:', error);
      } finally {
        setSearchingProducts(false);
      }
    };

    const debounce = setTimeout(searchProducts, 300);
    return () => clearTimeout(debounce);
  }, [productSearch]);

  // Close product dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target)) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle product selection
  const selectProduct = (product) => {
    setFormData(prev => ({
      ...prev,
      productId: product.id,
      productName: product.name,
      productCode: product.product_code,
      costPrice: product.cost_price,
      stockAvailable: product.stock_quantity
    }));
    setProductSearch(product.name);
    setShowProductDropdown(false);
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.productId) {
      toast.error('Please select a product');
      return;
    }

    if (!formData.quantity || parseInt(formData.quantity) <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    if (!formData.reason.trim()) {
      toast.error('Please enter a reason');
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(`${API_URL}/stock-damages`, {
        productId: formData.productId,
        quantity: parseInt(formData.quantity),
        damageType: formData.damageType,
        reason: formData.reason.trim(),
        evidence: formData.evidence
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Stock damage recorded successfully');
      setShowCreateForm(false);
      resetForm();
      fetchDamages();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to record damage');
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this damage record? Stock will be restored.')) return;

    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${API_URL}/stock-damages/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Damage deleted and stock restored');
      setShowDetailModal(false);
      fetchDamages();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete damage');
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      productId: '',
      productName: '',
      quantity: '',
      damageType: 'breakage',
      reason: '',
      evidence: []
    });
    setProductSearch('');
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get damage type style
  const getDamageTypeStyle = (type) => {
    return DAMAGE_TYPES.find(t => t.value === type)?.color || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-secondary-offWhite p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <FileWarning className="w-8 h-8 text-red-500" />
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-primary-black">Stock Damages</h1>
                <p className="text-secondary-gray text-sm">Track breakages, spoilage, and losses</p>
              </div>
            </div>

            {isAdmin && (
              <button
                onClick={() => setShowCreateForm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-gold/90 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Record Damage
              </button>
            )}
          </div>

          {/* Filters */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-gray w-5 h-5" />
              <input
                type="text"
                placeholder="Search by code or product..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
              />
            </div>

            <select
              value={filters.damageType}
              onChange={(e) => setFilters(prev => ({ ...prev, damageType: e.target.value }))}
              className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
            >
              <option value="">All Types</option>
              {DAMAGE_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>

            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
              placeholder="Start Date"
            />

            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
              placeholder="End Date"
            />
          </div>

          {/* View Toggle */}
          <div className="mt-4 flex items-center gap-2">
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'table' ? 'bg-accent-gold text-primary-black' : 'bg-secondary-offWhite text-secondary-gray'}`}
            >
              <List className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-accent-gold text-primary-black' : 'bg-secondary-offWhite text-secondary-gray'}`}
            >
              <Grid className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-accent-gold" />
            <p className="mt-2 text-secondary-gray">Loading damages...</p>
          </div>
        ) : damages.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg">
            <AlertTriangle className="w-12 h-12 mx-auto text-secondary-gray opacity-50" />
            <p className="mt-2 text-secondary-gray">No damage records found</p>
          </div>
        ) : viewMode === 'table' ? (
          /* Table View */
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-primary-black text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Code</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Product</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Type</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Qty</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Value</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-lightGray">
                  {damages.map(damage => (
                    <tr key={damage.id} className="hover:bg-secondary-offWhite transition-colors">
                      <td className="px-4 py-3 font-medium">{damage.damage_code}</td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium">{damage.product_name}</p>
                          <p className="text-xs text-secondary-gray">{damage.product_code}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getDamageTypeStyle(damage.damage_type)}`}>
                          {damage.damage_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium">{damage.quantity}</td>
                      <td className="px-4 py-3 text-right text-red-600 font-medium">
                        {formatCurrency(damage.damage_value)}
                      </td>
                      <td className="px-4 py-3 text-sm text-secondary-gray">
                        {formatDate(damage.recorded_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => {
                              setSelectedDamage(damage);
                              setShowDetailModal(true);
                            }}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => handleDelete(damage.id)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* Grid View */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {damages.map(damage => (
              <div
                key={damage.id}
                onClick={() => {
                  setSelectedDamage(damage);
                  setShowDetailModal(true);
                }}
                className="bg-white rounded-lg shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="font-bold text-primary-black">{damage.damage_code}</span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getDamageTypeStyle(damage.damage_type)}`}>
                    {damage.damage_type}
                  </span>
                </div>
                <p className="font-medium text-primary-black">{damage.product_name}</p>
                <p className="text-sm text-secondary-gray mb-2">{damage.product_code}</p>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-secondary-lightGray">
                  <div>
                    <p className="text-xs text-secondary-gray">Quantity</p>
                    <p className="font-bold">{damage.quantity}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-secondary-gray">Value</p>
                    <p className="font-bold text-red-600">{formatCurrency(damage.damage_value)}</p>
                  </div>
                </div>
                <p className="text-xs text-secondary-gray mt-2">{formatDate(damage.recorded_at)}</p>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
              disabled={pagination.page === 1}
              className="p-2 rounded-lg bg-white border border-secondary-lightGray disabled:opacity-50"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="px-4 py-2 bg-white rounded-lg border border-secondary-lightGray">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
              disabled={pagination.page === pagination.totalPages}
              className="p-2 rounded-lg bg-white border border-secondary-lightGray disabled:opacity-50"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Create Damage Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-primary-black">Record Stock Damage</h2>
                  <button
                    onClick={() => {
                      setShowCreateForm(false);
                      resetForm();
                    }}
                    className="p-2 hover:bg-secondary-offWhite rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Product Search */}
                  <div ref={productDropdownRef} className="relative">
                    <label className="block text-sm font-medium text-primary-black mb-1">
                      Product <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-gray w-5 h-5" />
                      <input
                        type="text"
                        value={productSearch}
                        onChange={(e) => setProductSearch(e.target.value)}
                        onFocus={() => productSearch && setShowProductDropdown(true)}
                        placeholder="Search products..."
                        className="w-full pl-10 pr-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                      />
                    </div>
                    {showProductDropdown && productResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-secondary-lightGray rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {productResults.map(product => (
                          <button
                            key={product.id}
                            type="button"
                            onClick={() => selectProduct(product)}
                            className="w-full px-4 py-2 text-left hover:bg-secondary-offWhite transition-colors border-b border-secondary-lightGray last:border-b-0"
                          >
                            <p className="font-medium">{product.name}</p>
                            <p className="text-xs text-secondary-gray">{product.product_code} • Stock: {product.stock_quantity}</p>
                          </button>
                        ))}
                      </div>
                    )}
                    {formData.productId && (
                      <p className="text-xs text-green-600 mt-1">✓ Selected: {formData.productName}</p>
                    )}
                  </div>

                  {/* Quantity */}
                  <div>
                    <label className="block text-sm font-medium text-primary-black mb-1">
                      Quantity <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.quantity}
                      onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
                      min="1"
                      max={formData.stockAvailable || 99999}
                      className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                      placeholder="Enter quantity"
                    />
                    {formData.stockAvailable && (
                      <p className="text-xs text-secondary-gray mt-1">Available: {formData.stockAvailable}</p>
                    )}
                  </div>

                  {/* Damage Type */}
                  <div>
                    <label className="block text-sm font-medium text-primary-black mb-1">
                      Damage Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.damageType}
                      onChange={(e) => setFormData(prev => ({ ...prev, damageType: e.target.value }))}
                      className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                    >
                      {DAMAGE_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Reason */}
                  <div>
                    <label className="block text-sm font-medium text-primary-black mb-1">
                      Reason <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.reason}
                      onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                      rows={3}
                      className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold resize-none"
                      placeholder="Describe what happened..."
                    />
                  </div>

                  {/* TODO: Evidence Upload - Cloudinary integration */}

                  {/* Submit */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateForm(false);
                        resetForm();
                      }}
                      className="flex-1 px-4 py-2 bg-secondary-offWhite text-primary-black font-semibold rounded-lg hover:bg-secondary-lightGray transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-gold/90 transition-colors"
                    >
                      Record Damage
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {showDetailModal && selectedDamage && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-primary-black">{selectedDamage.damage_code}</h2>
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      setSelectedDamage(null);
                    }}
                    className="p-2 hover:bg-secondary-offWhite rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-secondary-gray">Product</p>
                    <p className="font-medium">{selectedDamage.product_name}</p>
                    <p className="text-sm text-secondary-gray">{selectedDamage.product_code}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-secondary-gray">Type</p>
                      <span className={`inline-block px-2 py-1 text-sm font-semibold rounded-full ${getDamageTypeStyle(selectedDamage.damage_type)}`}>
                        {selectedDamage.damage_type}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-secondary-gray">Quantity</p>
                      <p className="font-bold">{selectedDamage.quantity}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-secondary-gray">Loss Value</p>
                    <p className="text-xl font-bold text-red-600">{formatCurrency(selectedDamage.damage_value)}</p>
                  </div>

                  <div>
                    <p className="text-sm text-secondary-gray">Reason</p>
                    <p className="text-primary-black">{selectedDamage.reason}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-secondary-lightGray">
                    <div>
                      <p className="text-sm text-secondary-gray">Recorded By</p>
                      <p className="font-medium">{selectedDamage.recorded_by_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-secondary-gray">Date</p>
                      <p className="font-medium">{formatDate(selectedDamage.recorded_at)}</p>
                    </div>
                  </div>
                </div>

                {isAdmin && (
                  <div className="flex gap-3 mt-6 pt-4 border-t border-secondary-lightGray">
                    <button
                      onClick={() => handleDelete(selectedDamage.id)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-600 font-semibold rounded-lg hover:bg-red-100 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockDamagePage;
